import React, { PropsWithChildren, useMemo } from 'react';
import { ActionsBar, Column } from '@ydistri/ds';
import ToggleArchivedAdministrationItemButton from '../../../common/buttons/ToggleArchivedAdministrationItemButton';
import DeleteIconButton from '../../../../../components/buttons/DeleteIconButton';
import { transformToPascalCase } from '@ydistri/generic-utils';
import EditIconButton from '../../../../../components/buttons/EditIconButton';
import EditContentIconButton from '../../../../../components/buttons/EditContentIconButton';
import { EntityListSourceType } from '@ydistri/api-sdk';
import { EntityListTypeSupported, getEntityListSpecification } from '../../entityListsLib';

interface AdministrationItemActionsProps extends PropsWithChildren {
  entityListId: number;
  entityListType: EntityListTypeSupported;
  entityListSourceType?: EntityListSourceType;
  itemName: string;
  disabled?: boolean;
  editableContent?: boolean;
  deletable?: boolean;
  archivable?: boolean;
  archived?: boolean;
  onDelete?: () => void;
  onToggleArchived?: () => void;
  onEdit?: () => void;
  onContentEdit?: () => void;
  deleteTooltip?: string;
}

const AdministrationItemActions: React.FC<AdministrationItemActionsProps> = ({
  entityListSourceType,
  entityListType,
  itemName,
  archivable,
  archived,
  onToggleArchived,
  disabled,
  editableContent,
  deletable,
  onDelete,
  onEdit,
  onContentEdit,
  deleteTooltip,
  children,
}) => {
  const entityListImpl = getEntityListSpecification(entityListType);
  const customMessages = entityListImpl.customMessages;

  const toggleArchiveButton = useMemo(() => {
    if (onToggleArchived) {
      //eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- archivable may be false here, we need to check other variables
      const archiveActionEnabled = archived || archivable;
      return (
        <ToggleArchivedAdministrationItemButton
          itemName={itemName}
          onClick={onToggleArchived}
          disabled={
            //eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- disabled may be false here, we need to check other variables
            disabled || !archiveActionEnabled || entityListSourceType !== EntityListSourceType.User
          }
          archived={archived}
        />
      );
    }
    return null;
  }, [entityListSourceType, onToggleArchived, itemName, disabled, archivable, archived]);

  const deletableButton = useMemo(() => {
    if (onDelete) {
      const title = `Delete ${itemName}`;
      const tooltip = deleteTooltip ?? `${title}...`;
      return (
        <DeleteIconButton
          size="small"
          tooltip={tooltip}
          onClick={onDelete}
          //eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- disabled may be false here, we need to check other variables
          disabled={disabled || !deletable}
          data-testid={transformToPascalCase(title)}
        />
      );
    }
  }, [onDelete, itemName, deleteTooltip, disabled, deletable]);

  const editButton = useMemo(() => {
    if (!archived && onEdit) {
      const title = `Edit ${itemName} Attributes`;
      return (
        <EditIconButton
          size="small"
          tooltip={title}
          onClick={onEdit}
          disabled={disabled}
          data-testid={transformToPascalCase(title)}
        />
      );
    }
  }, [archived, onEdit, itemName, disabled]);

  const editContentButton = useMemo(() => {
    if (!archived && onContentEdit) {
      const forbidden = disabled === true || !editableContent;
      let title: React.ReactNode = `Edit ${itemName} Content `;
      if (forbidden) {
        title = `Content of this list is not editable at the moment.`;
      }
      if (!editableContent && customMessages?.nonEditableContentReasons) {
        if (customMessages.nonEditableContentReasons.length > 0) {
          title = (
            <Column>
              <span>{title}</span>
              <ul>
                {customMessages.nonEditableContentReasons.map(reason => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </Column>
          );
        }
      }

      return (
        <EditContentIconButton
          size="small"
          tooltip={title}
          onClick={onContentEdit}
          disabled={forbidden}
          data-testid="edit_content_button"
        />
      );
    }
  }, [archived, disabled, editableContent, customMessages, itemName, onContentEdit]);

  const isEntityListUserType = entityListSourceType === EntityListSourceType.User;

  return (
    <ActionsBar>
      {isEntityListUserType && editContentButton}
      {isEntityListUserType && editButton}
      {isEntityListUserType && deletableButton}
      {isEntityListUserType && toggleArchiveButton}
      {children}
    </ActionsBar>
  );
};

export default AdministrationItemActions;
